import { isEqual } from "lodash";
import { applyPatchToState } from "./applyPatchToState";
import { hasPath, pickPaths, readPath, removePaths } from "./pathUtils";
import type {
  CoordinatorConfig,
  CoordinatorListener,
  FieldError,
  SyncStatus,
} from "./types";

/**
 * Should the field error at `key` be cleared because `patch` carries a real
 * user edit at that leaf?
 *
 * Wholesale-array patches reach every leaf via `hasPath`, including unchanged
 * siblings — clearing those would flicker the error off and back on across one
 * round-trip. We require the leaf value to *actually change* by deep equality.
 * Comparing values (not references) works for any leaf shape. Array-typed
 * leaves matter especially because `flush()` strips errored paths from
 * `sendable`, so a corrected array would never reach the server if we left
 * this case to the post-flush `clearFieldErrorsForSentPaths` fallback alone.
 */
function shouldClearErrorOnPatch(
  key: string,
  optimisticBefore: Partial<Record<string, unknown>>,
  patch: Partial<Record<string, unknown>>,
): boolean {
  if (!hasPath(patch, key)) return false;
  if (!hasPath(optimisticBefore, key)) return false;
  return !isEqual(readPath(optimisticBefore, key), readPath(patch, key));
}

export class EntitySaveCoordinator<
  TState extends Record<string, unknown>,
  TFields extends Record<string, unknown> = Partial<TState>,
> {
  private status: SyncStatus = "saved";
  private serverState: TState;
  private pendingChanges: Partial<TFields> = {};
  private inFlightFields: Partial<TFields> = {};
  private fieldErrors = new Map<string, FieldError>();
  private listeners = new Set<CoordinatorListener>();
  private cachedOptimisticState: TState | null = null;
  private readonly saveFn: CoordinatorConfig<TState, TFields>["save"];
  private readonly onErrorFn: CoordinatorConfig<TState, TFields>["onError"];
  private readonly parseFieldErrorsFn: CoordinatorConfig<TState, TFields>["parseFieldErrors"];

  constructor(config: CoordinatorConfig<TState, TFields>) {
    this.serverState = config.initialState;
    this.saveFn = config.save;
    this.onErrorFn = config.onError;
    this.parseFieldErrorsFn = config.parseFieldErrors;
  }

  enqueue(fields: Partial<TFields>): void {
    const optimisticBefore = this.getOptimisticState() as Partial<Record<string, unknown>>;
    const patch = fields as Partial<Record<string, unknown>>;
    for (const key of Array.from(this.fieldErrors.keys())) {
      if (shouldClearErrorOnPatch(key, optimisticBefore, patch)) {
        this.fieldErrors.delete(key);
      }
    }
    this.pendingChanges = applyPatchToState(this.pendingChanges, fields) as Partial<TFields>;
    if (this.status === "saved" || this.status === "error") {
      void this.flush();
    } else {
      this.emitChange();
    }
  }

  setServerState(state: TState): void {
    if (Object.keys(this.inFlightFields).length > 0) return;
    this.serverState = state;
    this.emitChange();
  }

  subscribe = (listener: CoordinatorListener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getOptimisticState = (): TState => {
    if (this.cachedOptimisticState === null) {
      const withInFlight = applyPatchToState(this.serverState, this.inFlightFields);
      this.cachedOptimisticState = applyPatchToState(withInFlight, this.pendingChanges);
    }
    return this.cachedOptimisticState;
  };

  getEntityStatus(): SyncStatus {
    return this.status;
  }

  getFieldStatus(field: string): SyncStatus {
    if (this.hasFieldErrorAtOrBelow(field)) return "error";
    if (hasPath(this.inFlightFields, field)) return "saving";
    if (hasPath(this.pendingChanges, field)) return "pending";
    return "saved";
  }

  private hasFieldErrorAtOrBelow(field: string): boolean {
    const childPrefix = `${field}.`;
    for (const key of this.fieldErrors.keys()) {
      if (key === field || key.startsWith(childPrefix)) return true;
    }
    return false;
  }

  getFieldError(field: string): FieldError | undefined {
    return this.fieldErrors.get(field);
  }

  hasUnsavedChanges(): boolean {
    return Object.keys(this.pendingChanges).length > 0 || Object.keys(this.inFlightFields).length > 0;
  }

  private async flush(): Promise<void> {
    if (Object.keys(this.pendingChanges).length === 0) {
      this.status = this.fieldErrors.size > 0 ? "error" : "saved";
      this.emitChange();
      return;
    }

    // Don't auto-resend leaves the server has already rejected — they would
    // poison every subsequent flush. The user must retype to clear the error;
    // enqueue() drops the field error for that path and the next flush picks
    // the value up.
    const errorPaths = Array.from(this.fieldErrors.keys());
    const sendable = errorPaths.length > 0
      ? removePaths(
          this.pendingChanges as Record<string, unknown>,
          errorPaths,
        ) as Partial<TFields>
      : this.pendingChanges;

    if (Object.keys(sendable).length === 0) {
      // Park in "error" state — user retype re-triggers flush via enqueue().
      this.status = "error";
      this.emitChange();
      return;
    }

    this.status = "saving";
    this.inFlightFields = sendable;
    // Errored paths stay in pendingChanges so the user's input remains
    // visible (optimistic state still merges them on top of serverState).
    this.pendingChanges = errorPaths.length > 0
      ? pickPaths(
          this.pendingChanges as Record<string, unknown>,
          errorPaths,
        ) as Partial<TFields>
      : ({} as Partial<TFields>);
    this.emitChange();

    try {
      const result = await this.saveFn(this.inFlightFields);
      this.serverState = result;
      // Clear errors only for paths we actually just sent. A field error on
      // some other path (e.g. a sibling the user hasn't retyped yet) survives.
      this.clearFieldErrorsForSentPaths();
      this.inFlightFields = {};
      if (Object.keys(this.pendingChanges).length > 0) {
        void this.flush();
      } else {
        this.status = this.fieldErrors.size > 0 ? "error" : "saved";
      }
    } catch (error: unknown) {
      const parsed = this.parseFieldErrorsFn?.(error);
      if (parsed != null) {
        // Validation response: replace error entries for the paths we sent
        // (those were under server review this round), then merge the new set.
        this.clearFieldErrorsForSentPaths();
        for (const [k, v] of parsed) this.fieldErrors.set(k, v);
      }
      this.pendingChanges = applyPatchToState(this.inFlightFields, this.pendingChanges) as Partial<TFields>;
      this.inFlightFields = {};
      this.status = "error";
      this.onErrorFn?.(error);
    }
    this.emitChange();
  }

  private clearFieldErrorsForSentPaths(): void {
    for (const key of Array.from(this.fieldErrors.keys())) {
      if (hasPath(this.inFlightFields, key)) this.fieldErrors.delete(key);
    }
  }

  private emitChange(): void {
    this.cachedOptimisticState = null;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
